if (!customElements.get('pra-gift-finder')) {
  class PraGiftFinder extends HTMLElement {
    connectedCallback() {
      this.eventController?.abort();
      this.eventController = new AbortController();
      const { signal } = this.eventController;
      this.modal = this.querySelector('[data-finder-modal]');
      this.steps = [...this.querySelectorAll('[data-finder-step]')];
      this.closeButtons = [...this.querySelectorAll('[data-finder-close]')];
      this.directDropdowns = [...this.querySelectorAll('[data-finder-dropdown]')];
      this.dynamicSubmit = this.querySelector('[data-finder-dynamic-submit]');
      this.dynamicFiltersLoaded = this.dynamicFiltersLoaded || false;
      this.filterRequestController = null;
      this.currentStep = 0;
      this.handleDocumentClick = (event) => {
        const button = event.target.closest('[data-finder-open]');
        if (!button) return;
        event.preventDefault();
        this.lastOpenButton = button;
        this.open();
      };

      document.addEventListener('click', this.handleDocumentClick, { signal });
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close(), { signal }));
      this.querySelectorAll('[data-finder-next]').forEach((button) => {
        button.addEventListener('click', () => this.next(), { signal });
      });
      this.querySelectorAll('[data-finder-back]').forEach((button) => {
        button.addEventListener('click', () => this.back(), { signal });
      });
      this.querySelectorAll('[data-finder-choice]').forEach((button) => {
        button.addEventListener('click', () => this.choose(button), { signal });
      });
      this.querySelectorAll('[data-finder-submit]').forEach((button) => {
        button.addEventListener('click', () => this.submit(), { signal });
      });
      this.dynamicSubmit?.addEventListener('click', () => this.submitDynamic(), { signal });
      this.querySelectorAll('[data-finder-direct-submit]').forEach((button) => {
        button.addEventListener('click', () => this.submitDirect(), { signal });
      });
      this.querySelectorAll('[data-finder-explore]').forEach((link) => {
        link.addEventListener('click', (event) => this.explore(event, link), { signal });
      });
      this.querySelectorAll('[data-finder-toggle]').forEach((button) => {
        button.addEventListener('click', () => this.toggleDropdown(button), { signal });
      });
      this.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-finder-chip]');
        if (chip && this.contains(chip)) {
          this.toggleDynamicChip(chip);
          return;
        }
        const option = event.target.closest('[data-finder-option]');
        if (option && this.contains(option)) this.chooseDirectOption(option);
      }, { signal });
      this.handleOutsideClick = (event) => {
        if (!event.target.closest('[data-finder-dropdown]')) this.closeDropdowns();
      };
      document.addEventListener('click', this.handleOutsideClick, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.querySelector('[data-finder-dropdown].is-open')) {
          this.closeDropdowns();
          return;
        }
        this.close();
      }, { signal });
      if (new URLSearchParams(window.location.search).get('open_gift_finder') === '1') {
        this.openFrame = window.requestAnimationFrame(() => this.open());
      } else {
        // Warm the hidden dialog after first-load resources, or immediately on open().
        this.scheduleFilterWarmup = () => {
          if (this.dynamicFiltersLoaded || this.dynamicFiltersLoading) return;
          if ('requestIdleCallback' in window) {
            this.filterIdleCallback = window.requestIdleCallback(() => this.loadDynamicFilters(), { timeout: 2000 });
          } else {
            this.filterWarmupTimer = window.setTimeout(() => this.loadDynamicFilters(), 1200);
          }
        };
        if (document.readyState === 'complete') this.scheduleFilterWarmup();
        else window.addEventListener('load', this.scheduleFilterWarmup, { once: true, signal });
      }
    }

    disconnectedCallback() {
      this.eventController?.abort();
      this.cancelFilterWarmup();
      window.cancelAnimationFrame(this.openFrame);
      this.stopFinderGallery();
      this.filterRequestController?.abort();
      this.dynamicRequestController?.abort();
      this.dynamicFiltersLoading = false;
      if (this.modal && !this.modal.hidden) document.body.classList.remove('overflow-hidden');
    }

    cancelFilterWarmup() {
      window.removeEventListener('load', this.scheduleFilterWarmup);
      if (this.filterIdleCallback !== undefined) window.cancelIdleCallback(this.filterIdleCallback);
      window.clearTimeout(this.filterWarmupTimer);
    }

    open() {
      if (!this.modal) return;
      this.cancelFilterWarmup();
      const drawer = document.querySelector('#Details-menu-drawer-container[open]');
      if (drawer) {
        const drawerComponent = drawer.closest('header-drawer, menu-drawer');
        const summary = drawer.querySelector(':scope > summary');
        if (typeof drawerComponent?.closeMenuDrawer === 'function' && summary) {
          drawerComponent.closeMenuDrawer(new Event('click'), summary);
          summary.setAttribute('aria-expanded', 'false');
        } else {
          drawer.removeAttribute('open');
          drawer.classList.remove('menu-opening');
          summary?.setAttribute('aria-expanded', 'false');
          document.querySelector('.section-header')?.classList.remove('menu-open');
          document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-mobile');
        }
      }
      this.modal.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.currentStep = 0;
      this.render();
      this.loadDynamicFilters();
      this.startFinderGallery();
      this.modal.querySelector('button')?.focus();
    }

    close() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.classList.remove('overflow-hidden');
      this.stopFinderGallery();
      this.lastOpenButton?.focus();
    }

    async loadDynamicFilters() {
      if (this.dynamicFiltersLoaded || this.dynamicFiltersLoading || !this.querySelector('[data-finder-dynamic-filters]')) return;
      this.dynamicFiltersLoading = true;
      const controller = new AbortController();
      this.dynamicRequestController = controller;
      const loading = this.querySelector('[data-finder-loading]');
      const error = this.querySelector('[data-finder-load-error]');
      const filters = this.querySelector('[data-finder-dynamic-filters]');
      try {
        const cacheKey = 'pra-gift-finder-filters-v1';
        let sourceHtml = '';
        try {
          const cached = JSON.parse(window.sessionStorage.getItem(cacheKey) || 'null');
          if (cached?.html && Date.now() - cached.savedAt < 300000) sourceHtml = cached.html;
        } catch (cacheError) {
          // Reading and removing storage can both throw in privacy mode.
        }
        if (!sourceHtml) {
          const response = await fetch(this.dataset.filterSourceUrl, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`Gift Finder source: ${response.status}`);
          sourceHtml = await response.text();
          if (controller.signal.aborted) return;
          try {
            window.sessionStorage.setItem(cacheKey, JSON.stringify({ savedAt: Date.now(), html: sourceHtml }));
          } catch (cacheError) {
            // Storage can be unavailable in privacy mode; the live response still works.
          }
        }
        const parsed = new DOMParser().parseFromString(sourceHtml, 'text/html');
        ['recipient', 'occasion'].forEach((kind) => {
          const target = this.querySelector(`[data-finder-options="${kind}"]`);
          const source = parsed.querySelector(`[data-filter-kind="${kind}"]`);
          if (!target) return;
          target.replaceChildren(...[...(source?.querySelectorAll('[data-finder-source-option]') || [])].map((item) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'pra-v2-modal__chip';
            button.dataset.finderChip = '';
            button.dataset.kind = kind;
            button.dataset.value = item.dataset.value || '';
            button.dataset.filterParam = item.dataset.filterParam || '';
            button.setAttribute('aria-pressed', 'false');
            button.innerHTML = `<span>${this.escapeHtml(item.dataset.label || item.textContent.trim())}</span><span class="pra-v2-modal__chip-remove" aria-hidden="true">×</span>`;
            return button;
          }));
        });
        const optionCount = this.querySelectorAll('[data-finder-chip]').length;
        if (!optionCount) throw new Error('Gift Finder has no active Search & Discovery values');
        this.dynamicFiltersLoaded = true;
        if (loading) loading.hidden = true;
        if (error) error.hidden = true;
        if (filters) filters.hidden = false;
      } catch (loadError) {
        if (controller.signal.aborted) return;
        console.warn(loadError);
        if (loading) loading.hidden = true;
        if (error) error.hidden = false;
      } finally {
        if (this.dynamicRequestController === controller) this.dynamicFiltersLoading = false;
      }
    }

    escapeHtml(value) {
      const span = document.createElement('span');
      span.textContent = value;
      return span.innerHTML;
    }

    toggleDynamicChip(button) {
      button.setAttribute('aria-pressed', String(button.getAttribute('aria-pressed') !== 'true'));
      const hasSelection = Boolean(this.querySelector('[data-finder-chip][aria-pressed="true"]'));
      if (this.dynamicSubmit) this.dynamicSubmit.disabled = !hasSelection;
      const hint = this.querySelector('[data-finder-hint]');
      if (hint) hint.hidden = hasSelection;
    }

    submitDynamic() {
      const selected = [...this.querySelectorAll('[data-finder-chip][aria-pressed="true"]')];
      if (!selected.length) return;
      const url = new URL(this.dataset.searchUrl || '/search?q=*', window.location.origin);
      selected.forEach((button) => {
        if (button.dataset.filterParam && button.dataset.value) {
          url.searchParams.append(button.dataset.filterParam, button.dataset.value);
        }
      });
      url.searchParams.set('gift_finder', '1');
      window.location.href = url.toString();
    }

    startFinderGallery() {
      const gallery = this.querySelector('[data-finder-gallery]');
      const track = gallery?.querySelector('[data-finder-gallery-track]');
      if (!gallery || !track || gallery.dataset.autoplay !== 'true' || track.children.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.stopFinderGallery();
      if (!track.querySelector('[data-finder-gallery-clone]')) {
        [...track.children].forEach((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.finderGalleryClone = '';
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('id');
          clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          track.append(clone);
        });
      }
      this.galleryFrame = window.requestAnimationFrame(() => {
        const firstClone = track.querySelector('[data-finder-gallery-clone]');
        if (!firstClone) return;
        const distance = firstClone.offsetLeft - track.firstElementChild.offsetLeft;
        const originalCount = track.querySelectorAll(':scope > :not([data-finder-gallery-clone])').length;
        const secondsPerItem = Math.max(1.5, (Number(gallery.dataset.delay) || 4000) / 1000);
        track.style.setProperty('--finder-gallery-distance', `${distance}px`);
        track.style.setProperty('--finder-gallery-duration', `${originalCount * secondsPerItem}s`);
        track.classList.add('is-continuous');
      });
    }

    stopFinderGallery() {
      window.cancelAnimationFrame(this.galleryFrame);
      this.querySelector('[data-finder-gallery-track]')?.classList.remove('is-continuous');
    }

    choose(button) {
      const step = button.closest('[data-finder-step]');
      step?.querySelectorAll('[data-finder-choice]').forEach((item) => item.setAttribute('aria-pressed', 'false'));
      button.setAttribute('aria-pressed', 'true');
      step.dataset.value = button.dataset.value || '';
      step.querySelector('[data-finder-next], [data-finder-submit]')?.removeAttribute('disabled');
    }

    next() {
      if (this.currentStep < this.steps.length - 1) this.currentStep += 1;
      this.render();
    }

    back() {
      if (this.currentStep > 0) this.currentStep -= 1;
      this.render();
    }

    render() {
      this.steps.forEach((step, index) => {
        step.hidden = index !== this.currentStep;
      });
      const progress = this.querySelector('[data-finder-progress]');
      if (progress) progress.style.width = `${((this.currentStep + 1) / this.steps.length) * 100}%`;
    }

    submit() {
      const selections = this.steps
        .map((step) => {
          const choice = step.querySelector('[data-finder-choice][aria-pressed="true"]');
          if (!choice) return null;
          return {
            type: step.dataset.type || 'option',
            value: choice.dataset.value || '',
            url: choice.dataset.url || '',
            filterQuery: choice.dataset.filterQuery || '',
          };
        })
        .filter(Boolean);
      const destination = selections.map((item) => item.url).find(Boolean) || '';
      this.navigate(destination, selections);
    }

    getDirectSelection() {
      const selections = this.directDropdowns
        .map((dropdown) => {
          return {
            type: dropdown.dataset.type || 'option',
            value: dropdown.dataset.value || '',
            url: dropdown.dataset.url || '',
            filterQuery: dropdown.dataset.filterQuery || '',
          };
        })
        .filter((item) => item.value);
      const destination = selections.map((item) => item.url).find(Boolean) || '';
      return {
        selections,
        destination,
      };
    }

    navigate(destination, selections) {
      const fallback = this.dataset.fallbackUrl || '/collections/all';
      const target = destination || fallback;
      const url = new URL(target, window.location.origin);
      selections.forEach((item) => {
        if (item.filterQuery) {
          const configured = new URLSearchParams(item.filterQuery.replace(/^\?/, ''));
          configured.forEach((value, key) => url.searchParams.append(key, value));
        } else {
          url.searchParams.set(`finder_${item.type}`, item.value);
        }
      });
      url.searchParams.set('finder', selections.map((item) => item.value).join('|'));
      window.location.href = url.toString();
    }

    submitDirect() {
      const direct = this.getDirectSelection();
      const recipient = direct.selections.find((item) => item.type === 'recipient');
      if (!recipient) {
        this.showRecipientError();
        return;
      }
      this.navigate(direct.destination, direct.selections);
    }

    explore(event, link) {
      const direct = this.getDirectSelection();
      if (!direct.selections.length) return;
      event.preventDefault();
      this.navigate(direct.destination || link.href, direct.selections);
    }

    toggleDropdown(button) {
      const dropdown = button.closest('[data-finder-dropdown]');
      const menu = dropdown?.querySelector('[data-finder-menu]');
      if (!dropdown || !menu) return;
      const willOpen = menu.hidden;
      this.closeDropdowns(dropdown);
      menu.hidden = !willOpen;
      button.setAttribute('aria-expanded', String(willOpen));
      dropdown.classList.toggle('is-open', willOpen);
      if (willOpen) menu.querySelector('[data-finder-option][aria-selected="true"], [data-finder-option]')?.focus();
    }

    async chooseDirectOption(button) {
      const dropdown = button.closest('[data-finder-dropdown]');
      if (!dropdown) return;
      dropdown.querySelectorAll('[data-finder-option]').forEach((option) => {
        option.setAttribute('aria-selected', String(option === button));
      });
      dropdown.dataset.value = button.dataset.value || '';
      dropdown.dataset.url = button.dataset.url || '';
      dropdown.dataset.filterQuery = button.dataset.filterQuery || '';
      const label = dropdown.querySelector('[data-finder-label]');
      if (label) label.textContent = button.textContent.trim();
      this.closeDropdowns();
      if (dropdown.dataset.type === 'recipient') {
        this.clearDirectError();
        await this.loadCollectionFilters(button.dataset.url || '');
      }
      dropdown.querySelector('[data-finder-toggle]')?.focus();
    }

    resetDynamicDropdown(dropdown, loading = false) {
      if (!dropdown) return;
      const toggle = dropdown.querySelector('[data-finder-toggle]');
      const menu = dropdown.querySelector('[data-finder-menu]');
      const label = dropdown.querySelector('[data-finder-label]');
      dropdown.dataset.value = '';
      dropdown.dataset.url = '';
      dropdown.dataset.filterQuery = '';
      dropdown.classList.toggle('is-loading', loading);
      dropdown.classList.add('is-disabled');
      if (toggle) toggle.disabled = true;
      if (label && menu) label.textContent = loading ? menu.dataset.loading : menu.dataset.placeholder;
      if (menu) menu.innerHTML = '';
    }

    setDynamicOptions(dropdown, options) {
      if (!dropdown) return;
      const toggle = dropdown.querySelector('[data-finder-toggle]');
      const menu = dropdown.querySelector('[data-finder-menu]');
      const label = dropdown.querySelector('[data-finder-label]');
      dropdown.classList.remove('is-loading');
      if (!menu || !toggle || !label) return;
      menu.replaceChildren(...options);
      const hasOptions = options.length > 0;
      toggle.disabled = !hasOptions;
      dropdown.classList.toggle('is-disabled', !hasOptions);
      label.textContent = hasOptions ? menu.dataset.placeholder : menu.dataset.empty;
    }

    makeFilterOption(label, value, filterQuery) {
      const option = document.createElement('button');
      option.type = 'button';
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.dataset.finderOption = '';
      option.dataset.value = value;
      option.dataset.filterQuery = filterQuery;
      option.textContent = label;
      return option;
    }

    makeBudgetOptions(rangeMax) {
      if (!Number.isFinite(rangeMax) || rangeMax <= 0) return [];
      const first = Number(this.dataset.budgetFirst || 70);
      const second = Number(this.dataset.budgetSecond || 100);
      const options = [
        this.makeFilterOption(
          this.dataset.budgetLowLabel || `Até R$ ${first}`,
          `ate-${first}`,
          `filter.v.price.lte=${first}`
        ),
      ];
      if (rangeMax > first) {
        options.push(this.makeFilterOption(
          this.dataset.budgetMiddleLabel || `R$ ${first} a R$ ${second}`,
          `${first}-${second}`,
          `filter.v.price.gte=${first}&filter.v.price.lte=${second}`
        ));
      }
      if (rangeMax > second) {
        options.push(this.makeFilterOption(
          this.dataset.budgetHighLabel || `Acima de R$ ${second}`,
          `acima-${second}`,
          `filter.v.price.gte=${second}`
        ));
      }
      return options;
    }

    async loadCollectionFilters(collectionUrl) {
      const occasion = this.directDropdowns.find((item) => item.dataset.type === 'occasion');
      const budget = this.directDropdowns.find((item) => item.dataset.type === 'budget');
      this.resetDynamicDropdown(occasion, true);
      this.resetDynamicDropdown(budget, true);
      this.filterRequestController?.abort();
      this.filterRequestController = new AbortController();

      try {
        const url = new URL(collectionUrl, window.location.origin);
        const response = await fetch(url, { signal: this.filterRequestController.signal });
        if (!response.ok) throw new Error(`Gift Finder filters: ${response.status}`);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const occasionSource = parsed.querySelector('[data-filter-kind="occasion"]');
        const occasionOptions = [...(occasionSource?.querySelectorAll('[data-finder-option]') || [])]
          .map((source) => this.makeFilterOption(
            source.textContent.trim(),
            source.dataset.value || '',
            source.dataset.filterQuery || ''
          ));
        const rangeMax = Number(parsed.querySelector('[data-filter-kind="price"]')?.dataset.rangeMax || 0);
        this.setDynamicOptions(occasion, occasionOptions);
        this.setDynamicOptions(budget, this.makeBudgetOptions(rangeMax));
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.warn(error);
        this.setDynamicOptions(occasion, []);
        this.setDynamicOptions(budget, []);
      }
    }

    closeDropdowns(except = null) {
      this.directDropdowns.forEach((dropdown) => {
        if (dropdown === except) return;
        const menu = dropdown.querySelector('[data-finder-menu]');
        const toggle = dropdown.querySelector('[data-finder-toggle]');
        if (menu) menu.hidden = true;
        toggle?.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('is-open');
      });
    }

    showRecipientError() {
      const error = this.querySelector('[data-finder-error]');
      if (error) error.hidden = false;
      const recipient = this.directDropdowns.find((dropdown) => dropdown.dataset.type === 'recipient');
      recipient?.classList.add('has-error');
      recipient?.querySelector('[data-finder-toggle]')?.focus();
    }

    clearDirectError() {
      const error = this.querySelector('[data-finder-error]');
      if (error) error.hidden = true;
      this.directDropdowns.forEach((dropdown) => dropdown.classList.remove('has-error'));
    }
  }

  customElements.define('pra-gift-finder', PraGiftFinder);
}

const enhanceJudgeMeCardsCarousel = (root = document) => {
  const carousels = root.matches?.('.jdgm-cards-carousel') ? [root] : root.querySelectorAll('.jdgm-cards-carousel');
  carousels.forEach((carousel) => {
    carousel.querySelectorAll('.jdgm-card .jdgm-rating-section').forEach((ratingSection) => {
      if (ratingSection.querySelector('.pra-v2-verified-buyer')) return;
      const badge = document.createElement('span');
      badge.className = 'pra-v2-verified-buyer';
      badge.innerHTML = `<img src="${window.praV2OkIconUrl}" width="12" height="12" alt="" aria-hidden="true"><span>Verified Buyer</span>`;
      ratingSection.prepend(badge);
    });

    if (carousel.dataset.praEnhanced === 'true') return;
    carousel.dataset.praEnhanced = 'true';

    const content = carousel.querySelector('.jdgm-content');
    const track = carousel.querySelector('.jdgm-videos-container');
    if (content && track) {
      const previous = document.createElement('button');
      previous.className = 'pra-v2-review-arrow pra-v2-review-arrow--previous';
      previous.type = 'button';
      previous.setAttribute('aria-label', 'Comentários anteriores');
      previous.textContent = '‹';
      const next = document.createElement('button');
      next.className = 'pra-v2-review-arrow pra-v2-review-arrow--next';
      next.type = 'button';
      next.setAttribute('aria-label', 'Próximos comentários');
      next.textContent = '›';
      const moveByCards = (direction) => {
        const card = track.querySelector('.jdgm-card.jdgm-media-card:not(.jdgm-clone-before):not(.jdgm-clone-after)');
        if (!card) return;

        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const step = card.getBoundingClientRect().width + gap;
        const currentIndex = Math.round(track.scrollLeft / step);
        const maxIndex = Math.max(0, Math.floor((track.scrollWidth - track.clientWidth) / step));
        let targetIndex = currentIndex + direction * 2;
        if (targetIndex > maxIndex) targetIndex = 0;
        if (targetIndex < 0) targetIndex = maxIndex;
        track.scrollTo({ left: targetIndex * step, behavior: 'smooth' });
      };

      previous.addEventListener('click', () => moveByCards(-1));
      next.addEventListener('click', () => moveByCards(1));
      content.append(previous, next);
    }

    const moreLink = document.createElement('a');
    moreLink.className = 'pra-v2-reviews-more';
    moreLink.href = '/pages/reviews';
    moreLink.innerHTML = '<span>Ver Mais</span><span aria-hidden="true">→</span>';
    carousel.insertAdjacentElement('afterend', moreLink);
  });
};

enhanceJudgeMeCardsCarousel();
const pendingReviewCarousels = new Set();
let reviewCarouselFrame = 0;
new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    const carousel = mutation.target.closest?.('.jdgm-cards-carousel');
    if (carousel) pendingReviewCarousels.add(carousel);
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches('.jdgm-cards-carousel')) pendingReviewCarousels.add(node);
      node.querySelectorAll('.jdgm-cards-carousel').forEach((item) => pendingReviewCarousels.add(item));
    });
  });
  if (reviewCarouselFrame || !pendingReviewCarousels.size) return;
  reviewCarouselFrame = window.requestAnimationFrame(() => {
    reviewCarouselFrame = 0;
    const carousels = [...pendingReviewCarousels];
    pendingReviewCarousels.clear();
    carousels.forEach((carousel) => {
      if (carousel.isConnected) enhanceJudgeMeCardsCarousel(carousel);
    });
  });
}).observe(document.body, { childList: true, subtree: true });
